import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { useTheme } from '../App';
// Faster, smoother slot machine effect
function SlotMachineText({
  text,
  isTransitioning,
  speed = 'normal'
}: {
  text: string;
  isTransitioning: boolean;
  speed?: 'fast' | 'normal';
}) {
  const [displayText, setDisplayText] = useState(text);
  const prevTextRef = useRef(text);
  const scrambleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()';
  useEffect(() => {
    // If text hasn't changed, don't animate
    if (text === prevTextRef.current) {
      return;
    }
    prevTextRef.current = text;
    if (!isTransitioning) {
      setDisplayText(text);
      return;
    }
    // Animation speeds
    const scrambleSpeed = speed === 'fast' ? 10 : 15;
    const revealSpeed = speed === 'fast' ? 40 : 60;
    const scrambleCount = speed === 'fast' ? 2 : 3;
    let currentIndex = 0;
    const targetLength = text.length;
    const interval = setInterval(() => {
      if (currentIndex >= targetLength) {
        clearInterval(interval);
        setDisplayText(text);
        return;
      }
      let count = 0;
      const scrambleInterval = setInterval(() => {
        count++;
        if (count > scrambleCount) {
          clearInterval(scrambleInterval);
          setDisplayText(prev => {
            const chars = prev.split('');
            chars[currentIndex] = text[currentIndex];
            return chars.join('');
          });
          currentIndex++;
        } else {
          setDisplayText(prev => {
            const chars = prev.split('');
            if (text[currentIndex] === ' ') {
              chars[currentIndex] = ' ';
            } else {
              chars[currentIndex] = scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
            }
            return chars.join('');
          });
        }
      }, scrambleSpeed);
    }, revealSpeed);
    return () => clearInterval(interval);
  }, [text, isTransitioning, speed]);
  return <>{displayText}</>;
}
export function Hero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  const [showJapanese, setShowJapanese] = useState(true);
  const [currentLines, setCurrentLines] = useState(['CREATIVE DIRECTOR', 'DIGITAL ARTIST', 'UX/UI DESIGNER']);
  const [isRotating, setIsRotating] = useState(false);
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const textVariants = {
    hidden: {
      y: 100,
      opacity: 0
    },
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.33, 1, 0.68, 1]
      }
    })
  };
  const initialLines = [{
    japanese: 'クリエイティブディレクター',
    english: 'CREATIVE DIRECTOR'
  }, {
    japanese: 'デジタルアーティスト',
    english: 'DIGITAL ARTIST'
  }, {
    japanese: 'UX/UIデザイナー',
    english: 'UX/UI DESIGNER'
  }];
  const alternativeTexts = ['FRONT-END DEV', 'WEB3 BUILDER', 'NFT ARTIST', 'COOL STUFF CREATOR', 'DIGITAL COLLECTOR', 'PROJECT FOUNDER', 'CREATIVE DIRECTOR', 'DIGITAL ARTIST', 'UX/UI DESIGNER'];
  // Initial Japanese to English transition (faster)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowJapanese(false);
    }, 400);
    return () => clearTimeout(timer);
  }, []);
  // Start rotating text after 1 second (reduced from 2)
  useEffect(() => {
    const startRotationTimer = setTimeout(() => {
      setIsRotating(true);
    }, 1000);
    return () => clearTimeout(startRotationTimer);
  }, []);
  // Rotate one line randomly every 3 seconds (reduced from 4)
  useEffect(() => {
    if (!isRotating) return;
    const rotateInterval = setInterval(() => {
      // Pick a random line index (0, 1, or 2)
      const randomLineIndex = Math.floor(Math.random() * 3);
      // Get available texts (exclude all currently displayed lines)
      const availableTexts = alternativeTexts.filter(text => !currentLines.includes(text));
      // If no available texts (shouldn't happen with 9 texts and 3 lines), skip
      if (availableTexts.length === 0) return;
      // Pick a random text from available ones
      const newText = availableTexts[Math.floor(Math.random() * availableTexts.length)];
      // Update that specific line
      setCurrentLines(prev => {
        const newLines = [...prev];
        newLines[randomLineIndex] = newText;
        return newLines;
      });
    }, 3000);
    return () => clearInterval(rotateInterval);
  }, [isRotating, currentLines]);
  const handleScrollDown = () => {
    const workSection = document.getElementById('work');
    if (workSection) {
      workSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section ref={containerRef} className="min-h-screen flex flex-col justify-center px-4 md:px-12 pt-20 pb-12 relative overflow-hidden" data-id="element-81">
      {/* Content layer - no background here, Three.js is fixed on page */}
      <div className="max-w-7xl w-full mx-auto relative z-10" data-id="element-82">
        <div className="mb-8 overflow-hidden" data-id="element-83">
          <motion.p custom={0} variants={textVariants} initial="hidden" animate="visible" className="font-mono uppercase" style={{
          color: isDark ? '#999' : '#666',
          fontFamily: 'Space Mono, monospace',
          letterSpacing: '1.6px',
          fontSize: '16px',
          fontWeight: 'normal'
        }} data-id="element-84">
            Javier D'Amico
          </motion.p>
        </div>

        <div className="relative perspective-1000 overflow-visible" data-id="element-85">
          <h1 className={`text-[4.5vw] md:text-[5vw] leading-none font-black tracking-tighter overflow-visible ${isDark ? 'mix-blend-difference text-white' : 'text-black'}`} style={{
          paddingBottom: '0.5em'
        }} data-id="element-86">
            {/* Line 1 - Left aligned */}
            <motion.div style={{
            y: y1,
            opacity
          }} data-id="element-87">
              <motion.span custom={1} variants={textVariants} initial="hidden" animate="visible" className="block overflow-visible" style={{
              whiteSpace: 'nowrap',
              overflow: 'visible'
            }} data-id="element-88">
                {showJapanese ? <SlotMachineText text={initialLines[0].japanese} isTransitioning={false} speed="fast" data-id="element-89" /> : <SlotMachineText text={currentLines[0]} isTransitioning={true} speed="normal" data-id="element-90" />}
              </motion.span>
            </motion.div>

            {/* Line 2 - Left aligned (gradient removed) */}
            <motion.div style={{
            y: y2,
            opacity
          }} data-id="element-91">
              <motion.span custom={2} variants={textVariants} initial="hidden" animate="visible" className="block overflow-visible" style={{
              whiteSpace: 'nowrap',
              overflow: 'visible',
              color: isDark ? 'white' : '#666'
            }} data-id="element-92">
                {showJapanese ? <SlotMachineText text={initialLines[1].japanese} isTransitioning={false} speed="fast" data-id="element-93" /> : <SlotMachineText text={currentLines[1]} isTransitioning={true} speed="normal" data-id="element-94" />}
              </motion.span>
            </motion.div>

            {/* Line 3 - Left aligned */}
            <motion.div style={{
            y: y3,
            opacity
          }} data-id="element-95">
              <motion.span custom={3} variants={textVariants} initial="hidden" animate="visible" className="block overflow-visible" style={{
              whiteSpace: 'nowrap',
              overflow: 'visible'
            }} data-id="element-96">
                {showJapanese ? <SlotMachineText text={initialLines[2].japanese} isTransitioning={false} speed="fast" data-id="element-97" /> : <SlotMachineText text={currentLines[2]} isTransitioning={true} speed="normal" data-id="element-98" />}
              </motion.span>
            </motion.div>
          </h1>
        </div>

        <div className="mt-16 md:mt-24 flex flex-col md:flex-row justify-between items-start md:items-end gap-8" data-id="element-99">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1,
          duration: 1
        }} className="max-w-md" data-id="element-100">
            <p className="text-lg md:text-xl leading-relaxed" style={{
            color: isDark ? 'rgb(163, 163, 163)' : '#666'
          }} data-id="element-101">
              Crafting digital art, design, and immersive interactive
              experiences, from Buenos Aires to the world.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2,
          duration: 1
        }} className="flex flex-col items-center gap-2" data-id="element-102">
            <button onClick={handleScrollDown} className="flex flex-col items-center gap-2 transition-opacity duration-300 hover:opacity-70 cursor-pointer" aria-label="Scroll to work section" data-id="element-103">
              <span className="text-xs uppercase tracking-widest font-light" style={{
              color: isDark ? '#999' : '#666'
            }} data-id="element-104">
                Scroll Down
              </span>
              <motion.div animate={{
              y: [0, 8, 0]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-105">
                <ArrowDown className="w-4 h-4" style={{
                color: isDark ? '#999' : '#666'
              }} data-id="element-106" />
              </motion.div>
            </button>
          </motion.div>
        </div>
      </div>
    </section>;
}